// MODULES
import React from "react";

// HELPERS
import {createClassName, getPropertiesByPrefix, parseDraftDataPropsToFieldState, delay} from "@helpers/utils";
import fulfillmentCheck from "./SurvivorsProtection.fulfillment";
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import * as CobHelpers from "../../../Cob.helpers";
import OptionsList from "../../../components/OptionsList/OptionsList";
import EditableField from "../../../components/EditableField/EditableField";
import TextView from "../../../../../components/TextView/TextView";
import CheckboxWithName from "../../../components/CheckboxWithName/CheckboxWithName";

export default class SurvivorsProtection extends React.PureComponent {
	static companyExclusive = false;
	static privateExclusive = true;

	static fields = _getFields();
	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
    
    if (prevProps.draftData !== props.draftData) {
      if (props.draftData) {
        // get the draftData props specific to this section only
        const draftDataProps = getPropertiesByPrefix(props.draftData, ['futur_survivors_protection_']);
        // parse the draftData props into state.fields
        const newFieldsState = parseDraftDataPropsToFieldState(this.state.fields, draftDataProps);
        this.setState(
          {
            fields: {
              ...newFieldsState
            }
          },
          async () => {          
              await delay(200);
              onChange(id, {...this.state}, fulfillmentCheck(this.props, this.state));
          }
        );
      }
		}
	}

	render() {
		const {props, state} = this;
		const {title} = props;

		const classes = createClassName("Cob-Fees", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled,
		});

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					<OptionsList
						type="radio"
						orientation="vertical"
						name="futur_survivors_protection_beneficiary"
						options={state.fields.futur_survivors_protection_beneficiary.options}
						onChange={this._onOptionsListChange}
					/>

					{state.fields.futur_survivors_protection_beneficiary.options[5].checked && (
						<React.Fragment>
							<p style={{margin: "15px 0 10px 0"}}>
								Var god ange annat förordnande
							</p>

							<EditableField
								name="futur_survivors_protection_beneficiary_other"
								Component={TextView}
								maxLength={600}
								disableEnterKey={true}
								label={`${600 - state.fields.futur_survivors_protection_beneficiary_other.length} tecken kvar`}
								value={state.fields.futur_survivors_protection_beneficiary_other}
								valid={
									state.fields.futur_survivors_protection_beneficiary_other.length > 0 &&
									state.fields.futur_survivors_protection_beneficiary_other.length <= 570 ? true : null
								}
								onChange={this._onFieldChange}
								onBlur={this._onFieldBlur}
								resize="vertical"
							/>
						</React.Fragment>

					)}

					<CheckboxWithName
						label="Ej förfoganderätt (förmånstagaren ska inte få förfoganderätt över försäkringen)"
						name={"futur_survivors_protection_disposition_excluded"}
						value={"yes"}
						checked={state.fields.futur_survivors_protection_disposition_excluded.checked}
						onChange={this._onCheckboxChange}
						style={{marginTop: "40px"}}
					/>

					<h4 style={{fontWeight: "600", marginTop: "40px", marginBottom: "10px"}}>
						Enskild egendom
					</h4>

					<OptionsList
						type="radio"
						orientation="vertical"
						name="futur_survivors_protection_private_property"
						options={state.fields.futur_survivors_protection_private_property.options}
						onChange={this._onOptionsListChange}
					/>
				</div>
			</div>
		);
	}

	_fieldValidator = (name, value) => {
		return value.length > 0;
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onCheckboxChange = (name, checked) => {
		this.setState({
			fields: {
				...this.state.fields,
				[name]: {...this.state.fields[name], checked}
			}
		});
	};

	_onOptionsListChange = ({type, name}, option, index) => {
		const newOptions = [...this.state.fields[name].options].map((o, i) => {
			if (i === index) {
				return option;
			}
			else {
				return {...o, checked: type === "radio" ? false : o.checked};
			}
		});

		this.setState({
			fields: {
				...this.state.fields,
				[name]: {
					options: newOptions,
				},
			},
		}, () => fulfillmentCheck(this.props, this.state));
	};
}

function _getFields() {
	return [
		"futur_survivors_protection_beneficiary",
		"futur_survivors_protection_beneficiary_other",
		"futur_survivors_protection_disposition_excluded",
		"futur_survivors_protection_private_property",
	];
}

function _getInitialState() {
	if (this.props.prevState) return {...this.props.prevState};

	return {
		validFields: {},
		fields: {
			futur_survivors_protection_beneficiary: {
				options: [
					{label: "I första hand: mak(a/e)/sambo. I andra hand: barn. I tredje hand: arvingar.", value: "F10", checked: false},
					{label: "I första hand: barn. I andra hand: mak(a/e)/sambo. I tredje hand: arvingar.", value: "F11", checked: false},
					{label: "I första hand: barnbarn. I andra hand: arvingar.", value: "F12", checked: false},
					{label: "I första hand: barn. I andra hand: arvingar.", value: "F13", checked: false},
					{label: "Arvingar.", value: "F21", checked: false},
					{label: "Annat förordnande.", value: "F04", checked: false}
				]
			},

			futur_survivors_protection_beneficiary_other: "",

			futur_survivors_protection_disposition_excluded: {
				checked: false
			},

			futur_survivors_protection_private_property: {
				options: [
					{label: "Belopp, försäkring och avkastning ska bli förmånstagarens enskilda egendom", value: "TrueWithout", checked: false},
					{label: "Belopp, försäkring och avkastning ska bli förmånstagarens enskilda egendom men med rätt att förordna annorlunda", value: "TrueWith", checked: false},
					{label: "Belopp, försäkring och avkastning ska inte bli förmånstagarens enskilda egendom", value: "False", checked: false}
				]
			},
		}
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Futur/SurvivorsProtection/SurvivorsProtection.js